import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const Speakers = () => {
    const [speakers, setSpeakers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        const fetchSpeakers = async () => {
            try {
                const response = await api.get('/speakers?status=active');
                setSpeakers(response.data);
            } catch (error) {
                console.error("Error fetching", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSpeakers();
    }, []);

    const primaryColor = config?.primaryColor || '#9d4edd';

    // Get background style for modal (same as Layout)
    const bgStyle = config?.backgroundImage
        ? { backgroundImage: `url(${config.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { backgroundColor: '#1c0042' };

    const pageDesign = config?.pageDesigns?.speakers || {};
    const showTitle = pageDesign.showTitle !== false;
    const pageTitle = pageDesign.title || 'Nuestros Ponentes';
    const pageSubtitle = pageDesign.subtitle || '';

    return (
        <div className="animate-fadeIn pb-20 font-['Alliance-Light']">
            {showTitle && (
                <header className="mb-12 text-center">
                    <h1 className="text-4xl font-black text-white tracking-tight uppercase italic">{pageTitle}</h1>
                    <p className="text-white/80 font-medium mt-2 tracking-widest text-xs uppercase">{pageSubtitle}</p>
                    <div className="w-20 h-1 bg-white/20 mx-auto mt-6 rounded-full"></div>
                </header>
            )}

            {isLoading ? (
                <div className="flex justify-center py-20">
                    <div className="w-10 h-10 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {speakers.map((s, index) => {
                        const isOrangeName = index % 2 === 0;
                        const frameGradient = isOrangeName ? 'linear-gradient(to right, #033f83, #079acf, #65bade)' : 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)';

                        return (
                            <div
                                key={s.id}
                                className="flex flex-col items-center text-center group cursor-pointer"
                                onClick={() => setSelectedSpeaker(s)}
                            >
                                <div className="relative mb-4">
                                    <div
                                        className="absolute inset-0 animate-morph"
                                        style={{ background: frameGradient, transform: 'scale(1.15)', opacity: 0.95, zIndex: 0 }}
                                    ></div>
                                    <div className="w-32 h-32 sm:w-44 sm:h-44 rounded-full border-[6px] border-white overflow-hidden relative z-10 shadow-2xl transition-transform duration-500 group-hover:scale-105">
                                        <img
                                            src={s.photo_url || `https://ui-avatars.com/api/?name=${s.name}&background=random`}
                                            alt={s.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>

                                <h3
                                    style={{
                                        backgroundImage: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    }}
                                    className="text-[18px] sm:text-[22px] font-black leading-tight mt-2 transition-transform duration-300 group-hover:-translate-y-1"
                                >
                                    {s.name}
                                </h3>
                                <p className="text-[11px] font-bold mt-2 uppercase tracking-widest text-slate-800 px-4">{s.profession}</p>
                            </div>
                        );
                    })}
                    {speakers.length === 0 && (
                        <div className="col-span-full text-center py-20 bg-white/5 rounded-[40px] border-2 border-dashed border-white/20 text-white/50 font-black uppercase tracking-widest italic">
                            Próximamente
                        </div>
                    )}
                </div>
            )}

            {/* Speaker Detail Modal */}
            {selectedSpeaker && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-fadeIn">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setSelectedSpeaker(null)}
                    ></div>

                    {/* Modal Content */}
                    <div
                        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[50px] border border-slate-200 shadow-2xl animate-scaleUp bg-white"
                    >
                        <button
                            className="absolute top-6 right-8 text-slate-400 hover:text-black text-2xl z-10"
                            onClick={() => setSelectedSpeaker(null)}
                        >
                            ✕
                        </button>

                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-48 h-48 rounded-full border-[8px] border-slate-100 overflow-hidden mb-8 shadow-2xl">
                                <img
                                    src={selectedSpeaker.photo_url || `https://ui-avatars.com/api/?name=${selectedSpeaker.name}&background=random`}
                                    alt={selectedSpeaker.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <h2 className="text-4xl font-bold text-black mb-2"
                                style={{
                                    fontFamily: 'Drina, cursive',
                                    backgroundImage: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    color: 'transparent'
                                }}>{selectedSpeaker.name}</h2>
                            <p className="text-sm font-bold uppercase tracking-[0.2em] mb-8" style={{ color: primaryColor }}>{selectedSpeaker.profession}</p>

                            <div className="w-16 h-1 bg-slate-200 rounded-full mb-8"></div>

                            <div className="text-slate-700 leading-relaxed text-sm text-left max-w-lg whitespace-pre-wrap">
                                {selectedSpeaker.profile || "Sin biografía disponible."}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Speakers;
